import { useMemo } from "react";

import { calculateAverage, sanitizeSensorValue } from "../sensorUtils";
import type {
  KpiInput,
  TempAveragesEntry,
  TempTimeSeriesEntry,
  TempTimeSeriesPoint,
} from "./types";

type UsePropertiesKpiDataInput = Readonly<{
  tempAveragesData: TempAveragesEntry[] | null | undefined;
  tempTimeSeriesData: TempTimeSeriesEntry[] | null | undefined;
}>;

function buildSeries(points: Map<string, number[]>): number[] {
  return Array.from(points.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, temps]) => calculateAverage(temps))
    .filter((temp) => !Number.isNaN(temp));
}

function collectTimeSeries(
  points: TempTimeSeriesPoint[] | undefined,
  bucket: Map<string, number[]>
) {
  if (!points || !Array.isArray(points)) return;
  points.forEach((point) => {
    if (!point?.timestamp || point.values?.living_air_temperature === undefined) return;
    const temp = sanitizeSensorValue(point.values.living_air_temperature);
    if (Number.isNaN(temp)) return;
    if (!bucket.has(point.timestamp)) bucket.set(point.timestamp, []);
    bucket.get(point.timestamp)!.push(temp);
  });
}

export function usePropertiesKpiData({
  tempAveragesData,
  tempTimeSeriesData,
}: UsePropertiesKpiDataInput): KpiInput {
  return useMemo(() => {
    const validTemp7d = (tempAveragesData ?? [])
      .map((item) => item?.avgTemp7d)
      .filter((temp): temp is number => temp !== null && temp !== undefined && !Number.isNaN(temp));
    const averageIndoorTempLast7d =
      validTemp7d.length > 0
        ? validTemp7d.reduce((sum, temp) => sum + temp, 0) / validTemp7d.length
        : NaN;

    const validTemp24h = (tempAveragesData ?? [])
      .map((item) => item?.avgTemp24h)
      .filter((temp): temp is number => temp !== null && temp !== undefined && !Number.isNaN(temp));
    const averageIndoorTempLast24h =
      validTemp24h.length > 0
        ? validTemp24h.reduce((sum, temp) => sum + temp, 0) / validTemp24h.length
        : NaN;

    const timeSeriesMap7d = new Map<string, number[]>();
    const timeSeriesMap24h = new Map<string, number[]>();

    (tempTimeSeriesData ?? []).forEach((item) => {
      collectTimeSeries(item?.timeSeries7d, timeSeriesMap7d);
      collectTimeSeries(item?.timeSeries24h, timeSeriesMap24h);
    });

    return {
      averageIndoorTempLast7d,
      averageIndoorTempLast24h,
      series7d: buildSeries(timeSeriesMap7d),
      series24h: buildSeries(timeSeriesMap24h),
    };
  }, [tempAveragesData, tempTimeSeriesData]);
}
